'use client';

import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import { Button as RACButton } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { InternalSpinner } from '../internal/spinner/spinner.js';
import { SPINNER_SIZES } from './constants.js';
import type { ActionButtonProps, BaseButtonProps, SingleIconButtonProps } from './types.js';

export interface IconButtonProps extends BaseButtonProps, SingleIconButtonProps, ActionButtonProps {}

function IconButton(
  {
    icon,
    variant = 'secondary',
    tone = 'base',
    size = 'medium',
    isLoading,
    loadingLabel,
    isDisabled,
    fullWidth,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: IconButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  useProductionWarning(() => {
    if (isLoading && !loadingLabel) {
      console.warn('IconButton requires loadingLabel when isLoading is true for accessibility');
    }
  }, [isLoading, loadingLabel]);

  return (
    <RACButton
      {...props}
      ref={ref}
      className={clsx('cim-icon-button', UNSAFE_className)}
      style={UNSAFE_style}
      data-variant={variant}
      data-tone={tone}
      data-size={size}
      data-full-width={fullWidth || undefined}
      isDisabled={isDisabled}
      isPending={isLoading}
    >
      {({ isPending }) => (
        <>
          {icon}
          {isPending && <InternalSpinner size={SPINNER_SIZES[size]} aria-label={loadingLabel} />}
        </>
      )}
    </RACButton>
  );
}

/**
 * Displays an icon-only button that allows users to perform an action.
 *
 * See [button usage guidelines](https://ui.cimpress.io/components/button/).
 */
const _IconButton = withStyleProps(forwardRef(IconButton), 'IconButton');

export { _IconButton as IconButton };
